import type { ColorSchemePreference } from "@codeshare/theme";
import type { Command } from "../components/CommandPalette";
import type { Menu } from "../components/MenuBar";
import { SHORTCUTS, shortcutLabel } from "../hooks/use-shortcuts";

/** What commands act on; App provides it. */
export interface CommandContext {
  readOnly: boolean;
  hasFile: boolean;
  inRoom: boolean;
  running: boolean;
  /** Why the open file can't run, if it can't. */
  cannotRun: string | undefined;
  /** Why the open file's tests can't run, if they can't. */
  cannotTest: string | undefined;
  /** The open file's path, and the pinned entry point ("Always run …"), if any. */
  openPath: string;
  pinned: string | undefined;
  newFile: () => void;
  openFile: () => void;
  importFolder: () => void;
  rename: () => void;
  deleteFile: () => void;
  download: () => void;
  downloadAll: () => void;
  /** Runs a Monaco editor action (undo, find, comment…) in the editor. */
  editorAction: (id: string) => void;
  run: () => void;
  runTests: () => void;
  pin: (path: string | undefined) => void;
  stop: () => void;
  runOptions: () => void;
  quickOpen: () => void;
  commandPalette: () => void;
  showExplorer: () => void;
  togglePanel: (panel: "files" | "side" | "bottom") => void;
  toggleTerminal: () => void;
  cycleEditor: (by: 1 | -1) => void;
  changeLanguage: () => void;
  /** Picks the room's OCaml compiler (OCaml or OxCaml). */
  changeOcamlCompiler: () => void;
  share: () => void;
  leave: () => void;
  connectionSettings: () => void;
  profile: () => void;
  setTheme: (preference: ColorSchemePreference) => void;
  about: () => void;
  source: () => void;
}

const key = shortcutLabel;
const baseName = (path: string) => path.slice(path.lastIndexOf("/") + 1);

// Monaco's own bindings, shown in menus (they work while the editor has focus).
const MONACO_KEYS = {
  undo: { code: "KeyZ", mod: true },
  redo: { code: "KeyZ", mod: true, shift: true },
  find: { code: "KeyF", mod: true },
  replace: { code: "KeyH", mod: true, alt: true },
  comment: { code: "Slash", mod: true },
  selectAll: { code: "KeyA", mod: true },
  goToLine: { code: "KeyG", ctrl: true },
  format: { code: "KeyF", alt: true, shift: true },
} as const;
const monacoKey = (name: keyof typeof MONACO_KEYS) => key(MONACO_KEYS[name]).replace(/Slash$/, "/");

/**
 * Every command, for the command palette (label: "Category: Name") and the menu bar
 * (menuLabel). Unavailable ones are hidden from the palette and disabled in menus.
 */
export function appCommands(c: CommandContext): Command[] {
  const readOnly = c.readOnly ? "Read-only" : undefined;
  const noFile = c.hasFile ? undefined : "No file is open";
  return [
    // File
    {
      id: "new",
      label: "File: New File",
      menuLabel: "New File",
      run: c.newFile,
      unavailable: readOnly,
    },
    {
      id: "open",
      label: "File: Open File…",
      menuLabel: "Open File…",
      run: c.openFile,
      unavailable: readOnly,
    },
    {
      id: "importFolder",
      label: "File: Import Folder…",
      menuLabel: "Import Folder…",
      run: c.importFolder,
      unavailable: readOnly,
    },
    {
      id: "rename",
      label: "File: Rename…",
      menuLabel: "Rename…",
      run: c.rename,
      unavailable: readOnly ?? noFile,
    },
    {
      id: "delete",
      label: "File: Delete",
      menuLabel: "Delete",
      run: c.deleteFile,
      unavailable: readOnly ?? noFile,
    },
    {
      id: "download",
      label: "File: Download",
      menuLabel: "Download",
      run: c.download,
      unavailable: noFile,
    },
    {
      id: "downloadAll",
      label: "File: Download All as Zip",
      menuLabel: "Download All as Zip",
      run: c.downloadAll,
    },
    { id: "share", label: "File: Share (Copy Room Link)", menuLabel: "Share…", run: c.share },
    {
      id: "leave",
      label: "File: Leave Room",
      menuLabel: "Leave Room",
      run: c.leave,
      unavailable: c.inRoom ? undefined : "Not in a room",
    },
    // Edit
    {
      id: "undo",
      label: "Edit: Undo",
      menuLabel: "Undo",
      shortcut: monacoKey("undo"),
      run: () => c.editorAction("undo"),
      unavailable: noFile,
    },
    {
      id: "redo",
      label: "Edit: Redo",
      menuLabel: "Redo",
      shortcut: monacoKey("redo"),
      run: () => c.editorAction("redo"),
      unavailable: noFile,
    },
    {
      id: "find",
      label: "Edit: Find",
      menuLabel: "Find",
      shortcut: monacoKey("find"),
      run: () => c.editorAction("actions.find"),
      unavailable: noFile,
    },
    {
      id: "replace",
      label: "Edit: Replace",
      menuLabel: "Replace",
      shortcut: monacoKey("replace"),
      run: () => c.editorAction("editor.action.startFindReplaceAction"),
      unavailable: readOnly ?? noFile,
    },
    {
      id: "comment",
      label: "Edit: Toggle Line Comment",
      menuLabel: "Toggle Line Comment",
      shortcut: monacoKey("comment"),
      run: () => c.editorAction("editor.action.commentLine"),
      unavailable: readOnly ?? noFile,
    },
    {
      id: "format",
      label: "Edit: Format Document",
      menuLabel: "Format Document",
      shortcut: monacoKey("format"),
      run: () => c.editorAction("editor.action.formatDocument"),
      unavailable: readOnly ?? noFile,
    },
    {
      id: "selectAll",
      label: "Selection: Select All",
      menuLabel: "Select All",
      shortcut: monacoKey("selectAll"),
      run: () => c.editorAction("editor.action.selectAll"),
      unavailable: noFile,
    },
    // Go
    {
      id: "quickOpen",
      label: "Go to File…",
      menuLabel: "Go to File…",
      shortcut: key(SHORTCUTS.quickOpen),
      run: c.quickOpen,
    },
    {
      id: "goToLine",
      label: "Go to Line/Column…",
      menuLabel: "Go to Line/Column…",
      shortcut: monacoKey("goToLine"),
      run: () => c.editorAction("editor.action.gotoLine"),
      unavailable: noFile,
    },
    {
      id: "nextEditor",
      label: "View: Open Next Editor",
      menuLabel: "Next Editor",
      shortcut: key(SHORTCUTS.nextEditor),
      run: () => c.cycleEditor(1),
    },
    {
      id: "previousEditor",
      label: "View: Open Previous Editor",
      menuLabel: "Previous Editor",
      shortcut: key(SHORTCUTS.previousEditor),
      run: () => c.cycleEditor(-1),
    },
    // View
    {
      id: "commandPalette",
      label: "View: Command Palette",
      menuLabel: "Command Palette…",
      shortcut: key(SHORTCUTS.commandPalette),
      run: c.commandPalette,
    },
    {
      id: "explorer",
      label: "View: Show Explorer",
      menuLabel: "Explorer",
      shortcut: key(SHORTCUTS.showExplorer),
      run: c.showExplorer,
    },
    {
      id: "toggleFiles",
      label: "View: Toggle Primary Side Bar",
      menuLabel: "Toggle Primary Side Bar",
      shortcut: key(SHORTCUTS.toggleFiles),
      run: () => c.togglePanel("files"),
    },
    {
      id: "togglePeople",
      label: "View: Toggle People & Chat",
      menuLabel: "People & Chat",
      shortcut: key(SHORTCUTS.togglePeople),
      run: () => c.togglePanel("side"),
    },
    {
      id: "toggleTerminal",
      label: "View: Toggle Terminal",
      menuLabel: "Terminal",
      shortcut: key(SHORTCUTS.toggleTerminal),
      run: c.toggleTerminal,
    },
    {
      id: "language",
      label: "Change Language Mode",
      menuLabel: "Change Language Mode…",
      run: c.changeLanguage,
      unavailable: noFile,
    },
    {
      id: "ocamlCompiler",
      label: "OCaml: Select Compiler (OCaml or OxCaml)",
      run: c.changeOcamlCompiler,
      unavailable: readOnly,
    },
    // Run
    {
      id: "run",
      label: "Run: Run File",
      menuLabel: "Run File",
      shortcut: key(SHORTCUTS.run),
      run: c.run,
      unavailable: c.cannotRun,
    },
    {
      id: "stop",
      label: "Run: Stop",
      menuLabel: "Stop",
      run: c.stop,
      unavailable: c.running ? undefined : "Nothing is running",
    },
    {
      id: "runTests",
      label: "Run: Run Tests",
      menuLabel: "Run Tests",
      shortcut: key(SHORTCUTS.runTests),
      run: c.runTests,
      unavailable: c.cannotTest,
    },
    {
      id: "pinEntry",
      label: `Run: Always Run ${baseName(c.openPath)}`,
      menuLabel: `Always Run ${baseName(c.openPath)}`,
      run: () => c.pin(c.openPath),
      unavailable: c.pinned === c.openPath ? "Already the entry point" : undefined,
    },
    {
      id: "unpinEntry",
      label: "Run: Run the Open File Instead",
      menuLabel: "Run the Open File Instead",
      run: () => c.pin(undefined),
      unavailable: c.pinned ? undefined : "Run already runs the open file",
    },
    { id: "runOptions", label: "Run: Run Options…", menuLabel: "Run Options…", run: c.runOptions },
    // Preferences
    {
      id: "connection",
      label: "Preferences: Connection Settings…",
      menuLabel: "Connection Settings…",
      run: c.connectionSettings,
    },
    {
      id: "profile",
      label: "Preferences: Your Name and Color…",
      menuLabel: "Your Name and Color…",
      run: c.profile,
    },
    {
      id: "themeLight",
      label: "Preferences: Color Theme: Light",
      menuLabel: "Light Theme",
      run: () => c.setTheme("light"),
    },
    {
      id: "themeDark",
      label: "Preferences: Color Theme: Dark",
      menuLabel: "Dark Theme",
      run: () => c.setTheme("dark"),
    },
    {
      id: "themeSystem",
      label: "Preferences: Color Theme: Follow System",
      menuLabel: "System Theme",
      run: () => c.setTheme("system"),
    },
    // Help
    {
      id: "shortcuts",
      label: "Help: Keyboard Shortcuts",
      menuLabel: "Keyboard Shortcuts",
      run: c.commandPalette,
    },
    {
      id: "source",
      label: "Help: View Source on GitHub",
      menuLabel: "View Source on GitHub",
      run: c.source,
    },
    { id: "about", label: "Help: About", menuLabel: "About", run: c.about },
  ];
}

/** The menu bar, VS Code's order. */
export const MENUS: Menu[] = [
  {
    label: "File",
    items: [
      "new",
      "open",
      "importFolder",
      "-",
      "rename",
      "delete",
      "-",
      "download",
      "downloadAll",
      "-",
      "share",
      "leave",
    ],
  },
  {
    label: "Edit",
    items: ["undo", "redo", "-", "find", "replace", "-", "comment", "format", "selectAll"],
  },
  {
    label: "View",
    items: [
      "commandPalette",
      "-",
      "explorer",
      "togglePeople",
      "toggleTerminal",
      "toggleFiles",
      "-",
      "language",
      "-",
      "themeLight",
      "themeDark",
      "themeSystem",
    ],
  },
  { label: "Go", items: ["quickOpen", "goToLine", "-", "nextEditor", "previousEditor"] },
  {
    label: "Run",
    items: ["run", "runTests", "stop", "-", "pinEntry", "unpinEntry", "-", "runOptions"],
  },
  { label: "Help", items: ["shortcuts", "-", "connection", "profile", "-", "source", "about"] },
];
